import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Send, Mail, MapPin, Phone, Instagram } from "lucide-react";

const ARTISAN_IMG = "https://customer-assets.emergentagent.com/job_pharoh-love-flutes/artifacts/q6q4mbpi_Pharoh%20Love%20Flutes-a.jpg";

export function PageHeader({ eyebrow, title, subtitle }) {
  return (
    <section className="pt-32 md:pt-40 pb-12 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto">
        <p className="label-eyebrow mb-4 fade-up" data-testid="page-eyebrow">{eyebrow}</p>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight font-light leading-[1.02] fade-up" style={{ animationDelay: "0.1s" }} data-testid="page-title">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-6 text-[var(--si-text-2)] text-lg md:text-xl leading-relaxed max-w-3xl fade-up" style={{ animationDelay: "0.2s" }} data-testid="page-subtitle">
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}

export function InstrumentCard({ inst, index }) {
  return (
    <Link
      to={`/instrument/${inst.id}`}
      className="instrument-card relative fade-up block"
      style={{ animationDelay: `${index * 0.08}s` }}
      data-testid={`instrument-card-${inst.id}`}
    >
      <div className="relative overflow-hidden">
        {inst.featured && <span className="featured-pill">Featured</span>}
        {inst.image ? (
          <img src={inst.image} alt={inst.name} className="instrument-image" />
        ) : (
          <div className="instrument-image bg-[var(--si-bg)] flex items-center justify-center text-[var(--si-text-2)] text-sm">
            No image yet
          </div>
        )}
      </div>
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">{inst.name}</h3>
          {inst.price && (
            <span className="font-mono text-sm text-[var(--si-text-2)] whitespace-nowrap pt-2">{inst.price}</span>
          )}
        </div>
        {(inst.wood || inst.dimensions) && (
          <p className="label-eyebrow mb-4">{[inst.wood, inst.dimensions].filter(Boolean).join(" · ")}</p>
        )}
        {inst.description && (
          <p className="text-[var(--si-text-2)] leading-relaxed text-sm md:text-base mb-6 line-clamp-3">
            {inst.description}
          </p>
        )}
        <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--si-terracotta)]">
          {inst.audio ? "Listen & view details →" : "View details →"}
        </span>
      </div>
    </Link>
  );
}

export function Gallery({ items, emptyText = "No pieces here yet. Add some from the admin panel." }) {
  if (!items?.length) {
    return (
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <p className="text-[var(--si-text-2)] italic max-w-7xl mx-auto">{emptyText}</p>
      </section>
    );
  }
  return (
    <section className="px-6 md:px-12 lg:px-24 pb-24">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
        {items.map((it, i) => <InstrumentCard key={it.id} inst={it} index={i} />)}
      </div>
    </section>
  );
}

export function LoveFluteStory() {
  return (
    <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="love-story-section">
      <div className="max-w-4xl mx-auto bg-[var(--si-bg-dark)] text-[var(--si-text-light)] rounded-3xl p-10 md:p-16 shadow-2xl">
        <p className="label-eyebrow mb-4" style={{ color: "rgba(245,242,235,0.6)" }}>A Native American Legend</p>
        <h2 className="font-display text-3xl md:text-5xl font-light leading-tight mb-8">The Story of the Love Flute</h2>
        <div className="space-y-5 text-[#EFE7D6]/90 leading-relaxed italic text-lg">
          <p>A long time ago, there lived a young man who was very shy. He was brave in battle, and led the buffalo hunt with courage, yet when it came to speaking his love to the girl he wanted to marry, he was too shy to speak.</p>
          <p>He would stand helplessly, his eyes cast to the ground, while other young men stood with their courting blankets outside the tipi of the girl's father.</p>
          <p className="text-center text-sm tracking-widest uppercase text-[#EFE7D6]/60 not-italic">— To make a long story short —</p>
          <blockquote className="border-l-2 pl-6 py-2 not-italic" style={{ borderColor: "var(--si-terracotta)" }}>
            The Old Man told him:<br /><br />
            <span className="text-xl text-[#FAF6EC] leading-relaxed">"All the birds and animals helped to make this flute, and their voices sing within it. When you play this flute for the girl you love, all our voices sing with you. Your music will speak the words of love that your voice alone cannot."</span>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

export function EgyptianScale() {
  return (
    <section className="px-6 md:px-12 lg:px-24 pb-24">
      <div className="max-w-4xl mx-auto text-center">
        <p className="label-eyebrow mb-4">Tuned by hand</p>
        <h2 className="font-display text-3xl md:text-5xl font-light mb-4">The Egyptian Pentatonic Scale</h2>
        <p className="text-[var(--si-text-2)] text-lg max-w-2xl mx-auto mb-10">
          Every love flute is tuned to the sacred Egyptian pentatonic — available in five keys.
          Played intuitively, your fingers manipulate the holes to create ominous, woven tones that speak the words love cannot.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          {["D","E","G","A","B"].map((k) => (
            <div key={k} className="w-20 h-20 rounded-full flex items-center justify-center font-display text-3xl font-medium shadow-lg"
              style={{ background: "var(--si-terracotta)", color: "var(--si-text-light)" }}>
              {k}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WatchListen() {
  return (
    <section id="watch-listen" className="py-24 px-6 md:px-12 lg:px-24 bg-[var(--si-bg-2)]/40">
      <div className="max-w-7xl mx-auto">
        <p className="label-eyebrow mb-4">Watch · Listen</p>
        <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05] mb-4">
          See the craft, hear the voice.
        </h2>
        <p className="text-[var(--si-text-2)] text-lg max-w-2xl mb-14">
          Watch the making of a Pharaoh Love Flute, and how to play. For more videos, reels and recordings, visit the Connect page.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div className="rounded-2xl overflow-hidden bg-black aspect-video" data-testid="video-making">
            <iframe className="w-full h-full" src="https://www.youtube.com/embed/EhNTdgDtRhA" title="The making of Pharaoh love flutes" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
          <div className="rounded-2xl overflow-hidden bg-black aspect-video" data-testid="video-closeups">
            <iframe className="w-full h-full" src="https://www.youtube.com/embed/lp_03k6-WxA" title="Pharaoh Love Flutes - Close Ups" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </div>

        <h3 className="font-display text-2xl md:text-3xl font-light mb-6">How to play your Pharaoh Love Flute</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl overflow-hidden bg-black aspect-video" data-testid="video-tutorial-1">
            <iframe className="w-full h-full" src="https://www.youtube.com/embed/SOomuCng3iM" title="How to Play - Tutorial 1" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
          <div className="rounded-2xl overflow-hidden bg-black aspect-video" data-testid="video-tutorial-2">
            <iframe className="w-full h-full" src="https://www.youtube.com/embed/X2dkVE356YA" title="How to Play - Tutorial 2" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </div>
      </div>
    </section>
  );
}

export function CraftSection({ content }) {
  const c = content || {};
  return (
    <section id="about" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[var(--si-bg-2)]/40">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5">
          <img src={c.about_image || ARTISAN_IMG} alt="The artisan at work" className="w-full rounded-2xl object-cover aspect-[4/5]" data-testid="about-image" />
        </div>
        <div className="lg:col-span-7">
          <p className="label-eyebrow mb-4">{c.about_title ? "The Craft" : "About"}</p>
          <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05]" data-testid="about-title">{c.about_title || "The Craft"}</h2>
          <div className="mt-8 text-[var(--si-text-2)] text-lg leading-relaxed whitespace-pre-line" data-testid="about-body">{c.about_body}</div>
        </div>
      </div>
    </section>
  );
}

export function InquiryForm({ initialInstrumentType = "" }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", instrument_type: initialInstrumentType, message: "",
  });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      await api.post("/inquiries", form);
      setStatus({ ok: true, msg: "Thank you. Your message has been received — I'll reach back soon." });
      setForm({ name: "", email: "", phone: "", instrument_type: "", message: "" });
    } catch {
      setStatus({ ok: false, msg: "Something went wrong. Please try again or email directly." });
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="inquiry-form">
      <div className="grid md:grid-cols-2 gap-4">
        <input required className="input-field" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inquiry-name" />
        <input required type="email" className="input-field" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inquiry-email" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <input className="input-field" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inquiry-phone" />
        <select className="input-field" value={form.instrument_type} onChange={(e) => setForm({ ...form, instrument_type: e.target.value })} data-testid="inquiry-type">
          <option value="">What are you drawn to?</option>
          <option value="drum">A drum</option>
          <option value="didgeridoo">A didgeridoo</option>
          <option value="flute">A Pharaoh love flute</option>
          <option value="both">Multiple / not sure</option>
        </select>
      </div>
      <textarea required className="input-field" placeholder="Tell me a little about what you're looking for — size, key, intention, anything that helps." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inquiry-message" />
      <button type="submit" disabled={submitting} className="btn-primary" data-testid="inquiry-submit">
        {submitting ? "Sending..." : "Send inquiry"} <Send size={16} />
      </button>
      {status && (
        <p className={`text-sm mt-3 ${status.ok ? "text-[var(--si-forest)]" : "text-red-700"}`} data-testid="inquiry-status">
          {status.msg}
        </p>
      )}
    </form>
  );
}

export function ContactBlock({ content }) {
  const c = content || {};
  return (
    <section id="contact" className="py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          {c.contact_email && (
            <div className="flex items-start gap-4" data-testid="contact-email">
              <Mail className="text-[var(--si-terracotta)] mt-1" size={20} />
              <div>
                <p className="label-eyebrow mb-1">Email</p>
                <a href={`mailto:${c.contact_email}`} className="text-lg hover:text-[var(--si-terracotta)]">{c.contact_email}</a>
              </div>
            </div>
          )}
          {c.contact_phone && (
            <div className="flex items-start gap-4" data-testid="contact-phone">
              <Phone className="text-[var(--si-terracotta)] mt-1" size={20} />
              <div>
                <p className="label-eyebrow mb-1">Phone</p>
                <a href={`tel:${c.contact_phone}`} className="text-lg hover:text-[var(--si-terracotta)]">{c.contact_phone}</a>
              </div>
            </div>
          )}
          {c.contact_location && (
            <div className="flex items-start gap-4" data-testid="contact-location">
              <MapPin className="text-[var(--si-terracotta)] mt-1" size={20} />
              <div>
                <p className="label-eyebrow mb-1">Studio</p>
                <p className="text-lg">{c.contact_location}</p>
              </div>
            </div>
          )}
          {c.contact_instagram && (
            <div className="flex items-start gap-4" data-testid="contact-instagram">
              <Instagram className="text-[var(--si-terracotta)] mt-1" size={20} />
              <div>
                <p className="label-eyebrow mb-1">Social</p>
                <a href="/connect" className="text-lg hover:text-[var(--si-terracotta)]">
                  Find me on Instagram, Facebook, YouTube & SoundCloud →
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function CommissionsBlock() {
  return (
    <section className="px-6 md:px-12 lg:px-24 pb-24">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <div className="admin-card p-6">
            <p className="text-3xl mb-2">🪵</p>
            <p className="font-display text-xl mb-1">Choose your wood</p>
            <p className="text-sm text-[var(--si-text-2)]">Black Maire, Rata, Pohutukawa, or a custom blend</p>
          </div>
          <div className="admin-card p-6">
            <p className="text-3xl mb-2">🎨</p>
            <p className="font-display text-xl mb-1">Choose your style</p>
            <p className="text-sm text-[var(--si-text-2)]">Custom design crafted to your taste — uniquely yours</p>
          </div>
          <div className="admin-card p-6">
            <p className="text-3xl mb-2">💎</p>
            <p className="font-display text-xl mb-1">$200 deposit</p>
            <p className="text-sm text-[var(--si-text-2)]">Reserve your spot · 4–6 weeks crafting time</p>
          </div>
        </div>
        <div className="admin-card p-8 md:p-10 rounded-2xl border border-[var(--si-border)] bg-[var(--si-bg)]">
          <InquiryForm />
        </div>
      </div>
    </section>
  );
}
